'use client'
// ---------------------------------------------------------------------------------
// Description:
// ---------------------------------------------------------------------------------
// This file contains the user menu component for the navbar.
// It also allows the user to select a company if they have multiple companies.
// It displays the user's name and allows them to log out.
// ---------------------------------------------------------------------------------



// Import types
import { Company } from '@/types/user';
import { MenuItem } from "@/types/uiElements";

// Import store
import store from "@/store";

// Import hooks
import React, { memo, useEffect, useCallback } from "react";
import { useCurrentUser } from '@/hooks/auth/useCurrentUser';
import { useSearchParams } from "@/utils/navigation";
import { useSnapshot, snapshot } from "valtio";


// Import components
import Spinner from "@/components/UiElements/Spiral";
import Button from "../UiElements/Button";
import Popover from "@/components/UiElements/PopOver";

// Import utility functions
import companyListForMenu from "@/components/NavBar/utils/companyListForMenu";




const UserMenu = () => {

    if (typeof window === 'undefined') return;

    const {logout, addStoreNameQuery } = useCurrentUser();
    const snap = useSnapshot(store);
    const currentUser = JSON.parse(JSON.stringify(snap.currentUser));
    const searchParams = useSearchParams();

    //const selectedCompanyObject = currentUser?.selectedCompany;



    // Log store state to console for debugging purposes
    useEffect(() => {
        (window as any).getStore = () => {
            console.log('store', snapshot(store));
            return snapshot(store)
        } ;
    }, []);




    // Callback function to handle company selection
    const handleSelectCompany = useCallback((company: Company) => {
        if (!company.id) {
            store.status.selectCompany = true;
            return;
        }
        if (company && snap.currentUser && store.currentUser) {
            store.status.selectCompany = false;
            const storeName = searchParams.get('storeName');
            if (storeName && company.store_name && storeName !== company.store_name) {
                window.open(`/?storeName=${company.store_name}`, '_blank', 'noopener,noreferrer');
            } else {
                store.currentUser.selectedCompany = company;
                addStoreNameQuery(company.store_name);
            }
        }
    }, [searchParams, snap.currentUser]);




    // Construct menu items
    const menuArray: MenuItem[] = [
        ...(currentUser?.companies
            ?
                [
                    {
                        title: <span className="capitalize">Store</span>,
                        icon: '',
                        className: 'font-medium hover:bg-white !cursor-default text-primary-600 select-none',
                    },
                    ...companyListForMenu(currentUser, handleSelectCompany),
                    { separator: true, className: 'my-[10px]' }
                ]
            :
                []
        ),
        {
            title: <span className="capitalize">{currentUser?.nickname}</span>,
            icon: '',
            className: 'font-medium hover:bg-white !cursor-default text-primary-600 select-none',
        },
        {
            title: 'Log out',
            icon: '',
            onClick: logout,
        },
    ];




    return (
        <>
            {currentUser && (
                <Popover
                    toggle={(open) => (
                        <Button
                            type="alt"
                            size="md"
                            className={`whitespace-nowrap !rounded-full capitalize bg-white !h-[40px] shadow-none active:shadow-focus z-[2] transition-all active:bg-primary-50 ${open ? '!bg-primary-50 !border-primary-500' : ''}`}
                        >
                            <div className="flex flex-row gap-sm">
                                <span className="font-icon-regular"></span>
                                <span className="font-medium">
                                    {currentUser?.selectedCompany?.name}
                                </span>
                            </div>
                        </Button>
                    )}
                    placement="bottom right"
                    offsetX={0}
                    offsetY={10}
                    menu={menuArray}
                />
            )}




            {(
                (currentUser && !currentUser?.selectedCompany) ||
                (currentUser && snap.status?.selectCompany && currentUser?.companies?.length > 1)
            ) && (
                <div className="z-[196] fixed bg-white w-[100vw] h-[100vh] top-0 left-0 flex justify-center items-center flex-col gap-lg overflow-y-auto py-[200px]">
                    <div className={
                        "w-[500px] shadow-xxl  border rounded-xl p-xl gap-xxl flex-col flex pb-xxl pr-xxl "+
                        `${currentUser?.companies?.length === 0 ? "bg-yellow-50 border-yellow-500" : "border-primary-500"}`
                    }>
                        <div className="flex flex-col gap-xs items-start select-none">
                            <span className="text-title-xl flex flex-row gap-md">
                                <span className="font-icon-regular w-[25px]"></span>
                                Choose Your Active Store
                            </span>
                            <span className="text-text-md text-gray-400 pl-[41px]">
                                Select the store you&apos;d like to work with for this session.
                            </span>
                        </div>
                        {
                            /*bg-yellow-50 p-md rounded-md border border-yellow-400*/
                        }
                        <div className="pl-[41px] flex flex-col gap-xs">
                            {currentUser?.companies?.length === 0 && (
                                <div className={" text-text-md gap-md flex flex-col"}>
                                    <div className={"text-title-md"}>Welcome, {currentUser.full_name}{currentUser.full_name ? "." : ""}</div>
                                    It looks like your account doesn’t have an assigned store. Please reach out to your administrator for assistance.
                                </div>
                            )}
                            {currentUser?.companies ? (
                                currentUser?.companies.map((company:Company) => (
                                    <Button
                                        size="sm"
                                        type="link"
                                        onClick={() => handleSelectCompany(company)}
                                        className="py-sm w-full group flex !flex-row !px-0 left-[-41px] relative"
                                        key={company.id}
                                    >
                                        <div className="flex flex-row gap-md">
                                            <span
                                                className={`font-icon-regular w-[25px] ${company.id !== currentUser?.selectedCompany?.id ? "opacity-0" : ""} group-hover:opacity-100 transition-all`}
                                            >
                                                
                                            </span>
                                            <span className="font-medium capitalize">
                                                {company.name}
                                                {company.id === currentUser?.selectedCompany?.id && <span className="font-normal text-gray-600">(Current)</span>}
                                            </span>
                                        </div>
                                    </Button>
                                ))
                            ) : (
                                <Spinner />
                            )}
                        </div>
                    </div>
                    <div
                        className="transition-all text-text-md select-none gap-md !py-lg font-normal items-start justify-start w-[500px] p-xl flex flex-row border-gray-200 border rounded-xl hover:border-primary-500 hover:text-primary-900 cursor-pointer text-gray-400 active:border-primary-500 active:text-primary-900 active:shadow-focus"
                        onClick={logout}
                    >
                        <span className="font-icon-regular w-[25px] text-center"></span>
                        <span className="!flex !flex-row gap-xs text-text-md">
                            <span>Log out of</span>
                            <span className="capitalize">{currentUser?.nickname}</span>
                        </span>
                    </div>
                </div>
            )}



        </>
    );
};

export default memo(UserMenu);
